import React, { useEffect, useState } from 'react';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const QuizQuestionview = () => {
  const { slug } = useParams(); // get slug from URL
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive'
  const [loadingQuestions, setLoadingQuestions] = useState({}); // Track loading state for each question
  const [responce, loading, error, callAPI, statusCode] = useApiCallHooks();

  // Fetch questions
  useEffect(() => {
    if (slug) {
      callAPI('get', `admin/get-quiz-questions/${slug}`);
    }
  }, [slug]);

  // Handle response for initial data
  useEffect(() => {
    if (statusCode === 200 && responce?.data) {
      setData(responce.data);
      setFilteredData(responce.data); // Initially show all questions
    }
  }, [statusCode, responce]);

  // Filter questions based on status
  useEffect(() => {
    if (filterStatus === 'all') {
      setFilteredData(data);
    } else if (filterStatus === 'active') {
      setFilteredData(data.filter(question => question.status));
    } else if (filterStatus === 'inactive') {
      setFilteredData(data.filter(question => !question.status));
    }
  }, [filterStatus, data]);

  // Function to toggle question status
  const handleToggleStatus = async (questionId, currentStatus) => {
    try {
      // Set loading state for the specific question
      setLoadingQuestions(prev => ({ ...prev, [questionId]: true }));
      const newStatus = !currentStatus;
      await callAPI('post', `admin/update-quiz-question-status`, {
        id: questionId,
        status: newStatus
      });

      // Update local state after successful API call
      if (statusCode === 200) {
        setData(data.map(question => 
          question.id === questionId 
            ? { ...question, status: newStatus }
            : question
        ));
        toast.success('Question status updated successfully!');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update question status.');
    } finally {
      // Clear loading state for the specific question
      setLoadingQuestions(prev => ({ ...prev, [questionId]: false }));
    }
  };

  return (
    <AdminAppBody 
      content={
        <div className="table-responsive">
          <div className="mb-3">
            <button 
              className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
              onClick={() => setFilterStatus('all')}
              disabled={loading}
            >
              All Questions
            </button>
            <button 
              className={`btn btn-sm ${filterStatus === 'active' ? 'btn-success' : 'btn-outline-success'} me-2`}
              onClick={() => setFilterStatus('active')}
              disabled={loading}
            >
              Active Questions
            </button>
            <button 
              className={`btn btn-sm ${filterStatus === 'inactive' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setFilterStatus('inactive')}
              disabled={loading}
            >
              Inactive Questions
            </button>
          </div>
          <table className="table table-bordered table-hover mt-3">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Question</th>
                <th>Option A</th>
                <th>Option B</th>
                <th>Option C</th>
                <th>Option D</th>
                <th>Correct</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No questions found.
                  </td>
                </tr>
              ) : (
                filteredData.map((question, index) => (
                  <tr key={question.id}>
                    <td>{index + 1}</td>
                    <td>{question.question}</td>
                    <td>{question.option1}</td>
                    <td>{question.option2}</td>
                    <td>{question.option3}</td>
                    <td>{question.option4}</td>
                    <td>{question.correct_answer}</td>
                    <td>
                      <span
                        className={`badge ${question.status ? 'bg-success' : 'bg-secondary'} cursor-pointer ${loadingQuestions[question.id] ? 'opacity-50' : ''}`}
                        onClick={() => !loadingQuestions[question.id] && handleToggleStatus(question.id, question.status)}
                        title={loadingQuestions[question.id] ? 'Updating...' : question.status ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {loadingQuestions[question.id] ? 'Loading...' : question.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      }
    />
  );
};

export default QuizQuestionview;